import { reactive, watch } from 'vue'
import type { SquadChangePayload } from '~/services/api/modules/squads/types'
import * as Sentry from '@sentry/nuxt'
import { analytics } from '~/_plugins/analytics/core'

export interface SquadFormErrors {
  name?: string
  tag?: string
  motto?: string
  avatar?: string
  cover?: string
}

export const useSquadFormEdit = (formState: SquadChangePayload) => {
  const errors = reactive<SquadFormErrors>({})
  const { $snacks } = useNuxtApp()

  const clearErrors = () => {
    errors.name = undefined
    errors.tag = undefined
    errors.motto = undefined
    errors.avatar = undefined
    errors.cover = undefined
  }

  const handleValidationErrors = (err: any) => {
    if (err?.response?.status === 422 && err?.response?._data) {
      const validationErrors = err.response._data as Record<string, string>

      if (validationErrors.name) {
        errors.name = validationErrors.name
      }
      if (validationErrors.tag) {
        errors.tag = validationErrors.tag
      }
      if (validationErrors.motto) {
        errors.motto = validationErrors.motto
      }
      if (validationErrors.avatar) {
        errors.avatar = validationErrors.avatar
      }
      if (validationErrors.cover) {
        errors.cover = validationErrors.cover
      }
    } else if (err?.response?.status === 400 && err?.response?._data) {
      $snacks.error(err?.response?._data?.message)
      analytics('LigaPass_CreateSquad_Error', {
        squad_name: formState.name
      })
    } else {
      Sentry.captureEvent({
        message: 'ERROR CREATE SQUAD',
        level: 'error',
        tags: {
          action: 'createSquad'
        },
        extra: { err }
      })
      analytics('LigaPass_CreateSquad_Error', {
        squad_name: formState.name
      })
    }
  }

  const onChangeAvatar = async (file: File) => {
    formState.avatar = file
    if (errors.avatar) {
      errors.avatar = undefined
    }
  }

  const clearAvatar = () => {
    formState.avatar = null
  }

  // Функции для работы с cover
  const onChangeCover = async (file: File) => {
    formState.cover = file
    if (errors.cover) {
      errors.cover = undefined
    }
  }

  const clearCover = () => {
    formState.cover = null
  }

  const selectTagColor = (id: number) => {
    formState.tag_color_id = id
  }

  const handleChangeTagName = (value: string) => {
    formState.tag = value
    if (errors.tag) {
      errors.tag = undefined
    }
  }

  const createFormData = (): FormData => {
    const payload = new FormData()

    payload.append('name', formState.name)
    payload.append('tag', formState.tag)
    payload.append('motto', formState.motto)

    if (formState.tag_color_id !== null) {
      payload.append('tag_color_id', String(formState.tag_color_id))
    }

    if (formState.avatar) {
      payload.append('avatar', formState.avatar)
    }

    if (formState.cover) {
      payload.append('cover', formState.cover)
    }

    return payload
  }

  watch(
    () => formState.name,
    () => {
      if (errors.name) {
        errors.name = undefined
      }
    }
  )

  watch(
    () => formState.motto,
    () => {
      if (errors.motto) {
        errors.motto = undefined
      }
    }
  )

  watch(
    () => formState.tag,
    () => {
      if (errors.tag) {
        errors.tag = undefined
      }
    }
  )

  return {
    errors,
    clearErrors,
    handleValidationErrors,
    onChangeAvatar,
    clearAvatar,
    onChangeCover,
    clearCover,
    selectTagColor,
    handleChangeTagName,
    createFormData
  }
}
